// header-account-popup
// Header account icon popup (snippets/header-account-popup.liquid).
// Hover mode (desktop >= 1230px with a mouse) is driven by CSS; this file adds
// Escape-to-dismiss there, and the tap-to-toggle sheet everywhere else.
//
// Deliberately an asset rather than a `{% javascript %}` block: the CLI's
// local build of `snippet-scripts.js` truncates the bundle when any snippet's
// JS contains multibyte characters, which broke this script in `theme dev`.

(function () {
  const popup = document.querySelector('[data-account-popup]')
  if (!popup) return

  const trigger = popup.querySelector('.account-popup__trigger')
  // Must match the hover-mode media query in the stylesheet above.
  const hoverQuery = window.matchMedia('(min-width: 1230px) and (hover: hover)')
  // How far the page may scroll before an open tap-mode popup closes.
  const SCROLL_CLOSE_THRESHOLD = 10
  let openScrollY = 0

  function isTapMode() {
    return !hoverQuery.matches
  }

  function isOpen() {
    return popup.hasAttribute('data-open')
  }

  function isFocusLeaving(event) {
    return !popup.contains(event.relatedTarget)
  }

  // ---- Hover mode (desktop): CSS opens it; Escape dismisses it ----

  function dismissPopup() {
    popup.setAttribute('data-dismissed', '')
    trigger?.focus()
  }

  function resetDismissed() {
    popup.removeAttribute('data-dismissed')
  }

  // ---- Tap mode (mobile/touch): the icon toggles it ----

  function openPopup() {
    renderSavedSizes()
    popup.setAttribute('data-open', '')
    resetDismissed()
    trigger.setAttribute('aria-expanded', 'true')
    openScrollY = window.scrollY
    document.addEventListener('click', handleOutsideClick, true)
    window.addEventListener('scroll', handleWindowScroll, { passive: true })
  }

  function closePopup() {
    if (!isOpen()) return
    popup.removeAttribute('data-open')
    trigger.setAttribute('aria-expanded', 'false')
    document.removeEventListener('click', handleOutsideClick, true)
    window.removeEventListener('scroll', handleWindowScroll)
  }

  // A tap on the dimmed page only closes the popup, so it doesn't also
  // follow whatever link sits under the scrim. Taps elsewhere in the header
  // (cart, menu) close it and still go through.
  function handleOutsideClick(event) {
    if (popup.contains(event.target)) return
    closePopup()
    if (event.target.closest('main-nav')) return
    event.preventDefault()
    event.stopPropagation()
  }

  function handleWindowScroll() {
    if (Math.abs(window.scrollY - openScrollY) > SCROLL_CLOSE_THRESHOLD) closePopup()
  }

  function handleTriggerClick(event) {
    if (!isTapMode()) return
    event.preventDefault()
    if (isOpen()) closePopup()
    else openPopup()
  }

  // Leaving the panel through a link (e.g. a same-page dashboard link that
  // only scrolls) should put the sheet away.
  function handlePanelLinkClick(event) {
    if (isTapMode() && event.target.closest('.account-popup__panel a')) closePopup()
  }

  function syncMode() {
    popup.toggleAttribute('data-tap-mode', isTapMode())
    if (isTapMode()) {
      trigger.setAttribute('aria-expanded', String(isOpen()))
      return
    }
    closePopup()
    trigger.removeAttribute('aria-expanded')
  }

  // ---- Edit saved size preferences ----

  const sizesPanel = popup.querySelector('[data-account-popup-sizes]')
  const sizeToggleButtons = Array.from(popup.querySelectorAll('[data-account-popup-sizes-toggle]'))
  const sizeDepartmentPanels = Array.from(popup.querySelectorAll('[data-account-popup-sizes-department]'))

  function isWomensDepartment(departmentPanel) {
    return departmentPanel.dataset.accountPopupSizesDepartment === 'womens'
  }

  function readDepartmentSizes(departmentPanel) {
    const preferences = window.TcSizePreferences
    if (!preferences) return {}
    const storageKey = isWomensDepartment(departmentPanel)
      ? preferences.WOMENS_STORAGE_KEY
      : preferences.STORAGE_KEY
    return preferences.readStoredSizes(storageKey) || {}
  }

  // A saved value outside the preset list still shows, rather than reading as blank.
  function ensureSelectHasOption(select, value) {
    if (!value || Array.from(select.options).some((option) => option.value === value)) return
    select.add(new Option(value, value))
  }

  function syncDepartmentSelects(departmentPanel) {
    const sizes = readDepartmentSizes(departmentPanel)
    for (const select of departmentPanel.querySelectorAll('[data-account-popup-size-key]')) {
      const value = sizes[select.dataset.accountPopupSizeKey] || ''
      ensureSelectHasOption(select, value)
      select.value = value
    }
  }

  function renderSavedSizes() {
    sizeDepartmentPanels.forEach(syncDepartmentSelects)
  }

  // Men's goes through commitSizePreference (Klaviyo + cart attribute sync); women's
  // stays local, same as everywhere else that saves women's sizes.
  function saveSizeFromSelect(select) {
    const preferences = window.TcSizePreferences
    if (!preferences) return
    const departmentPanel = select.closest('[data-account-popup-sizes-department]')
    const patch = { [select.dataset.accountPopupSizeKey]: select.value }

    if (isWomensDepartment(departmentPanel)) {
      preferences.commitWomensSizePreference(patch)
      return
    }
    const sizes = preferences.commitSizePreference(patch)
    document.dispatchEvent(new CustomEvent('tc:sizes-saved', { detail: { sizes } }))
  }

  function handleSizeSelectChange(event) {
    const select = event.target.closest('[data-account-popup-size-key]')
    if (select) saveSizeFromSelect(select)
  }

  function showSizeDepartment(department) {
    for (const button of sizeToggleButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.accountPopupSizesToggle === department))
    }
    for (const departmentPanel of sizeDepartmentPanels) {
      departmentPanel.hidden = departmentPanel.dataset.accountPopupSizesDepartment !== department
    }
  }

  function handleSizeToggleClick(event) {
    const button = event.target.closest('[data-account-popup-sizes-toggle]')
    if (button) showSizeDepartment(button.dataset.accountPopupSizesToggle)
  }

  // Size Preferences accordion
  function toggleSizesAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true'
    trigger.setAttribute('aria-expanded', String(!isExpanded))
  }

  function handleSizesAccordionClick(event) {
    const trigger = event.target.closest('[data-account-popup-sizes-trigger]')
    if (trigger) toggleSizesAccordion(trigger)
  }

  if (sizesPanel) {
    renderSavedSizes()
    sizesPanel.addEventListener('change', handleSizeSelectChange)
    sizesPanel.addEventListener('click', handleSizeToggleClick)
    sizesPanel.addEventListener('click', handleSizesAccordionClick)
    popup.addEventListener('mouseenter', renderSavedSizes)
    document.addEventListener('tc:sizes-saved', renderSavedSizes)
    window.addEventListener('storage', renderSavedSizes)
  }

  if (!trigger) return
  syncMode()
  hoverQuery.addEventListener('change', syncMode)
  trigger.addEventListener('click', handleTriggerClick)
  popup.addEventListener('click', handlePanelLinkClick)
  popup.querySelector('[data-account-popup-close]')?.addEventListener('click', function () {
    closePopup()
    trigger.focus()
  })

  popup.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return
    if (!isTapMode()) {
      dismissPopup()
      return
    }
    closePopup()
    trigger.focus()
  })
  popup.addEventListener('mouseleave', resetDismissed)
  // Tap mode closes only when focus really moved elsewhere (keyboard Tab):
  // tapping plain text in the panel blurs with no `relatedTarget`.
  // Hover mode skips the reset while hovered: a same-page dashboard link
  // moves focus to its card, and resetting then would reopen the popup
  // under the pointer.
  popup.addEventListener('focusout', function (event) {
    if (!isFocusLeaving(event)) return
    if (isTapMode()) {
      if (event.relatedTarget) closePopup()
      return
    }
    if (!popup.matches(':hover')) resetDismissed()
  })
})()
